import React, { lazy } from 'react';
import { UserMenu, dashboardPagesMenu, demoPagesMenu, pageLayoutTypesPagesMenu } from '../menu';
import Login from '../pages/presentation/auth/Login';
import Maintenance from '../pages/presentation/auth/Maintenance';
import PrivateRoute from './privateRoutes';

const LANDING = {
	DASHBOARD: lazy(() => import('../pages/presentation/dashboard/DashboardPage')),
	SLA_DASHBOARD: lazy(() => import('../pages/presentation/dashboard/SlaDashboard')),
};
const AUTH = {
	PAGE_404: lazy(() => import('../pages/presentation/auth/Page404')),
};
const PAGE_LAYOUTS = {
	HEADER_SUBHEADER: lazy(() => import('../pages/presentation/page-layouts/HeaderAndSubheader')),
	HEADER: lazy(() => import('../pages/presentation/page-layouts/OnlyHeader')),
	SUBHEADER: lazy(() => import('../pages/presentation/page-layouts/OnlySubheader')),
	CONTENT: lazy(() => import('../pages/presentation/page-layouts/OnlyContent')),
	BLANK: lazy(() => import('../pages/presentation/page-layouts/Blank')),
	ASIDE: lazy(() => import('../pages/presentation/aside-types/DefaultAsidePage')),
	MINIMIZE_ASIDE: lazy(() => import('../pages/presentation/aside-types/MinimizeAsidePage')),
};

const LEGAL = {
	LEGAL_MYLIST: lazy(() => import('../pages/presentation/legal/LegalMylist')),
	LEGAL_LEGALDEPT: lazy(() => import('../pages/presentation/legal/LegalLegaldept')),
	LEGAL_LEGALALL: lazy(() => import('../pages/presentation/legal/LegalLegalall')),
	LEGAL_PARTYLIST: lazy(() => import('../pages/presentation/legal/LegalPartylist')),
	LEGAL_LEGALQUEUE: lazy(() => import('../pages/presentation/legal/LegalLegalqueue')),
	LEGAL_LEGALDRAFT: lazy(() => import('../pages/presentation/legal/LegalLegaldraft')),
	LEGAL_LEGALDRAFT_DETAIL: lazy(() => import('../pages/presentation/legal/LegalLegaldraftDetail')),
	LEGAL_LEGALHEAD: lazy(() => import('../pages/presentation/legal/LegalLegalhead')),
	LEGAL_LEGALSIGNED: lazy(() => import('../pages/presentation/legal/LegalLegalsigned')),
	LEGAL_LEGALFA: lazy(() => import('../pages/presentation/legal/LegalLegalfa')),
	LEGAL_LEGALHEADADMIN: lazy(() => import('../pages/presentation/legal/LegalLegalheadadmin')),
	LEGAL_LEGALDUE: lazy(() => import('../pages/presentation/legal/LegalLegaldue'))
}

const SETTINGS = {
	USER: lazy(() => import('../pages/settings/UserSettings')),
	ROLE: lazy(() => import('../pages/settings/RoleSettings')),
	BRANCH: lazy(() => import('../pages/settings/BranchSettings')),
	DEPARTMENT: lazy(() => import('../pages/settings/DepartmentSettings')),
	DEPT_APPROVAL_PAGE: lazy(() => import('../pages/settings/DeptApprovalPage')),
	MASTERDATA: lazy(() => import('../pages/settings/MasterDataSettings')),
	PROFILE: lazy(() => import('../pages/settings/ProfileSettings')),
	UAR: lazy(() => import('../pages/settings/UarReview')),
	SLA: lazy(() => import('../pages/settings/SlaSettings')),
}

const NOTIF_PAGE = lazy(() => import('../pages/presentation/notification/NotificationPage'));

/**
 * Build route array dynamically at call time so UserMenu (populated after login)
 * is read fresh instead of stale from module load.
 */
export function buildRoutes() {
  return [
    /**
     * Landing
     */
    {
      path: UserMenu?.dashboard?.path || dashboardPagesMenu.dashboard.path,
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_MYLIST />
        </PrivateRoute>
      ),
    },
    {
      path: 'auth-pages/maintenance',
      element: <Maintenance />,
    },
    {
      path: demoPagesMenu.page404.path,
      element: <AUTH.PAGE_404 />,
    },
    {
      path: demoPagesMenu.login.path,
      element: <Login />,
    },
    {
      path: demoPagesMenu.signUp.path,
      element: <Login isSignUp />,
    },

    /** ************************************************** */

    /**
     * Page Layout Types
     */
    {
      path: pageLayoutTypesPagesMenu.blank.path,
      element: (
        <PrivateRoute>
          <PAGE_LAYOUTS.BLANK />
        </PrivateRoute>
      ),
    },
    {
      path: pageLayoutTypesPagesMenu.pageLayout.subMenu.headerAndSubheader.path,
      element: (
        <PrivateRoute>
          <PAGE_LAYOUTS.HEADER_SUBHEADER />
        </PrivateRoute>
      ),
    },
    {
      path: pageLayoutTypesPagesMenu.pageLayout.subMenu.onlyHeader.path,
      element: (
        <PrivateRoute>
          <PAGE_LAYOUTS.HEADER />
        </PrivateRoute>
      ),
    },
    {
      path: pageLayoutTypesPagesMenu.pageLayout.subMenu.onlySubheader.path,
      element: (
        <PrivateRoute>
          <PAGE_LAYOUTS.SUBHEADER />
        </PrivateRoute>
      ),
    },
    {
      path: pageLayoutTypesPagesMenu.pageLayout.subMenu.onlyContent.path,
      element: (
        <PrivateRoute>
          <PAGE_LAYOUTS.CONTENT />
        </PrivateRoute>
      ),
    },
    {
      path: pageLayoutTypesPagesMenu.asideTypes.subMenu.defaultAside.path,
      element: (
        <PrivateRoute>
          <PAGE_LAYOUTS.ASIDE />
        </PrivateRoute>
      ),
    },
    {
      path: pageLayoutTypesPagesMenu.asideTypes.subMenu.minimizeAside.path,
      element: (
        <PrivateRoute>
          <PAGE_LAYOUTS.MINIMIZE_ASIDE />
        </PrivateRoute>
      ),
    },

    /**
     * Legal pages — paths come from UserMenu (bridged via LABEL_TO_KEY in menu.ts)
     */
    {
      path: UserMenu?.sla_dashboard?.path || 'sla-dashboard',
      element: (
        <PrivateRoute>
          <LANDING.SLA_DASHBOARD />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_mylist?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_MYLIST />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_legaldept?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_LEGALDEPT />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_legalall?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_LEGALALL />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_partylist?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_PARTYLIST />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_legalqueue?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_LEGALQUEUE />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_legaldraft?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_LEGALDRAFT />
        </PrivateRoute>
      ),
    },
    {
      path: (UserMenu?.legal_legaldraft?.path || '') + '/:id',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_LEGALDRAFT_DETAIL />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_legalhead?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_LEGALHEAD />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_legalsigned?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_LEGALSIGNED />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_legalfa?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_LEGALFA />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_legalheadadmin?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_LEGALHEADADMIN />
        </PrivateRoute>
      ),
    },
    {
      path: UserMenu?.legal_legaldue?.path || '',
      element: (
        <PrivateRoute>
          <LEGAL.LEGAL_LEGALDUE />
        </PrivateRoute>
      ),
    },

    /** ************************************************** */

    /**
     * Settings Pages
     */
    {
      path: 'settings/user',
      element: (
        <PrivateRoute>
          <SETTINGS.USER />
        </PrivateRoute>
      ),
    },
    {
      path: 'settings/role',
      element: (
        <PrivateRoute>
          <SETTINGS.ROLE />
        </PrivateRoute>
      ),
    },
    {
      path: 'settings/branch',
      element: (
        <PrivateRoute>
          <SETTINGS.BRANCH />
        </PrivateRoute>
      ),
    },
    {
      path: 'settings/master-data',
      element: (
        <PrivateRoute>
          <SETTINGS.MASTERDATA />
        </PrivateRoute>
      ),
    },
    {
      path: 'settings/uar',
      element: (
        <PrivateRoute>
          <SETTINGS.UAR />
        </PrivateRoute>
      ),
    },
    {
      path: 'settings/department',
      element: (
        <PrivateRoute>
          <SETTINGS.DEPARTMENT />
        </PrivateRoute>
      ),
    },
    {
      path: 'settings/department/:id/approval',
      element: (
        <PrivateRoute>
          <SETTINGS.DEPT_APPROVAL_PAGE />
        </PrivateRoute>
      ),
    },
    {
      path: 'settings/profile',
      element: (
        <PrivateRoute>
          <SETTINGS.PROFILE />
        </PrivateRoute>
      ),
    },
    {
      path: 'settings/sla',
      element: (
        <PrivateRoute>
          <SETTINGS.SLA />
        </PrivateRoute>
      ),
    },

    /**
     * Notification Page
     */
    {
      path: 'notifications',
      element: (
        <PrivateRoute>
          <NOTIF_PAGE />
        </PrivateRoute>
      ),
    },
  ];
}
